import React from 'react';
import { Control, FormProvider, UseFormRegister, useForm, useFormContext } from 'react-hook-form';
import type { ProyectoFormDTO } from '../../../../api/Objetos';
import { seguimientoPlataformaApi } from '../../../../api/api';
import { createEmptyProyecto } from '../emptyProyecto';
import { JsonTableField, ToggleField } from '../../../../ui/form/fields';
import { FormSection, useProjectReadOnly } from '../projectForm.helpers';
import { SectionAlerts, SectionSaveButton } from '../sectionCrudUtils';
import { useRegisterSectionSave } from '../SectionSaveCoordinator';
import { useQuery } from "@tanstack/react-query";
import BarraCargando from "../../../../ui/BarraCargando";
import { useTranslation } from 'react-i18next';

export function SeguimientoPlataformaSection(props: { proyectoId: number; projectSlug: string; showIds?: boolean }) {
    const readOnly = useProjectReadOnly();
    const form = useForm<ProyectoFormDTO>({
        defaultValues: createEmptyProyecto()
    });
    const {
        data: plataformaData,
        refetch,
        isPending,
        error
    } = useQuery({
        queryKey: ['admin', 'proyectos', 'seguimiento-plataforma', props.proyectoId],
        queryFn: () => seguimientoPlataformaApi.get(props.proyectoId),
    });

    React.useEffect(() => {
        if (plataformaData) {
            form.reset({
                ...form.getValues(),
                seguimientoPlataforma: plataformaData
            });
        }
    }, [form, plataformaData]);

    const handleSave = form.handleSubmit(async (value) => {
        if (value.seguimientoPlataforma) {
            await seguimientoPlataformaApi.save(
                props.proyectoId,
                value.seguimientoPlataforma
            );
            await refetch();
        }
    });

    useRegisterSectionSave({
        id: 'seguimiento-plataforma',
        isDirty: form.formState.isDirty,
        isSaving: isPending,
        save: async () => {
            await handleSave();
        },
    });

    return (
        <FormProvider {...form}>
            <SeguimientoPlataformaSectionFields
                projectId={props.proyectoId}
                projectSlug={props.projectSlug}
                control={form.control}
                register={form.register}
                isLoading={isPending}
                loadError={error}
            />
            {!readOnly && <SectionSaveButton onClick={() => void handleSave()} busy={isPending} />}
        </FormProvider>
    );
}

function SeguimientoPlataformaSectionFields(props: {
    projectId: number;
    projectSlug: string;
    control: Control<ProyectoFormDTO>;
    register: UseFormRegister<ProyectoFormDTO>;
    isLoading: boolean;
    loadError: any;
}) {
    const { t } = useTranslation();
    const readOnly = useProjectReadOnly();
    return (
        <FormSection
            id="seguimiento-plataforma"
            title={t('seguimientoPlataformaSection.title')}
            collapsible
            defaultOpen={false}
            headerActions={!readOnly &&
                <div style={{display: 'flex', alignItems: 'center', gap: '1rem'}}>
                    {props.isLoading && <BarraCargando size="1.2em" />}
                    <ToggleField
                        label={t('seguimientoPlataformaSection.public')}
                        name="seguimientoPlataforma.publico"
                        register={props.register}
                    />
                </div>
            }
        >
            <SectionAlerts loadError={props.loadError}/>
            <JsonTableField
                projectId={props.projectId}
                projectSlug={props.projectSlug}
                label={t('seguimientoPlataformaSection.workInProgress')}
                name="seguimientoPlataforma.datosVariables"
                control={props.control}
                slug="seguimiento-plataforma"
                previewMode="link"
            />
        </FormSection>
    );
}